import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1E40AF', // KPRU Royal Blue (PTLE Standard)
      light: '#3B82F6',
      dark: '#1E3A8A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D97706', // Warm Education Gold (PTLE Standard)
      light: '#F59E0B',
      dark: '#B45309',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#059669',
      light: '#10B981',
      dark: '#047857',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#D97706',
      light: '#F59E0B',
      dark: '#B45309',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#0284C7',
      light: '#38BDF8',
      dark: '#0369A1',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#475569',
    },
    divider: 'rgba(226, 232, 240, 0.85)',
  },
  typography: {
    fontFamily: '"Sarabun", "Prompt", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontFamily: '"Prompt", sans-serif', fontWeight: 700, letterSpacing: '-0.02em', color: 'inherit' },
    h2: { fontFamily: '"Prompt", sans-serif', fontWeight: 700, letterSpacing: '-0.02em', color: 'inherit' },
    h3: { fontFamily: '"Prompt", sans-serif', fontWeight: 600, letterSpacing: '-0.015em', color: 'inherit' },
    h4: { fontFamily: '"Prompt", sans-serif', fontWeight: 700, letterSpacing: '-0.015em', color: 'inherit' },
    h5: { fontFamily: '"Prompt", sans-serif', fontWeight: 600, letterSpacing: '-0.01em', color: 'inherit' },
    h6: { fontFamily: '"Prompt", sans-serif', fontWeight: 600, color: 'inherit' },
    subtitle1: { fontFamily: '"Prompt", sans-serif', fontWeight: 600 },
    subtitle2: { fontFamily: '"Prompt", sans-serif', fontWeight: 600 },
    overline: { fontFamily: '"Prompt", sans-serif', fontWeight: 700, letterSpacing: '0.08em' },
    body1: { fontFamily: '"Sarabun", "Prompt", sans-serif', fontSize: '0.975rem', lineHeight: 1.65 },
    body2: { fontFamily: '"Sarabun", "Prompt", sans-serif', fontSize: '0.885rem', lineHeight: 1.6 },
    caption: { fontFamily: '"Sarabun", "Prompt", sans-serif', fontSize: '0.78rem', lineHeight: 1.5 },
    button: { fontFamily: '"Prompt", sans-serif', fontWeight: 500, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F8FAFC',
          color: '#0F172A',
        },
        '.bg-blue, [style*="background-color: rgb(30, 64, 175)"], [style*="#1E40AF"], [style*="#1e40af"], [style*="#1E3A8A"], [style*="#1e3a8a"], [style*="#2563EB"], [style*="#2563eb"], [style*="#1976D2"], [style*="#1976d2"]': {
          '&:not(.MuiChip-root):not(.MuiLinearProgress-bar)': {
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Prompt", sans-serif',
          borderRadius: 10,
          padding: '8px 20px',
          boxShadow: 'none',
          fontWeight: 500,
          textTransform: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.MuiButton-containedPrimary': {
            background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
            color: '#FFFFFF !important',
            '& *': {
              color: '#FFFFFF !important',
            },
            '&:hover': {
              background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)',
              color: '#FFFFFF !important',
            },
          },
          '&.MuiButton-containedSecondary': {
            background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #B45309 0%, #D97706 100%)',
            },
          },
        },
        sizeSmall: {
          padding: '5px 14px',
          borderRadius: 8,
          fontSize: '0.82rem',
        },
        sizeLarge: {
          padding: '11px 26px',
          borderRadius: 12,
          fontSize: '1rem',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(226, 232, 240, 0.85)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.06)',
          transition: 'box-shadow 0.25s ease, transform 0.25s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 14,
        },
        elevation1: {
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.05)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
        },
        elevation2: {
          boxShadow: '0 6px 24px rgba(15, 23, 42, 0.06)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
        },
        elevation3: {
          boxShadow: '0 8px 32px rgba(15, 23, 42, 0.07)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          border: '1px solid rgba(226, 232, 240, 0.85)',
          boxShadow: '0 4px 16px rgba(15, 23, 42, 0.03)',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(15, 23, 42, 0.15)',
            borderRadius: '4px',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& th': {
            fontFamily: '"Prompt", sans-serif',
            fontWeight: 600,
            fontSize: '0.84rem',
            backgroundColor: '#F1F5F9',
            color: '#1E293B',
            whiteSpace: 'nowrap',
            borderBottom: '1px solid #E2E8F0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(226, 232, 240, 0.8)',
          padding: '12px 16px',
          fontSize: '0.885rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(241, 245, 249, 0.55)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Prompt", sans-serif',
          borderRadius: 8,
          fontWeight: 500,
          '&.MuiChip-filledPrimary': {
            backgroundColor: '#1E40AF',
            color: '#FFFFFF !important',
            '& .MuiChip-icon, & .MuiChip-deleteIcon, & .MuiChip-label': {
              color: '#FFFFFF !important',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#94A3B8',
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(30, 64, 175, 0.12)',
          },
        },
        notchedOutline: {
          borderColor: '#CBD5E1',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Prompt", "Sarabun", sans-serif',
          fontSize: '0.9rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.16)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Prompt", sans-serif',
          fontWeight: 700,
          '& .MuiTypography-root': {
            color: 'inherit',
          },
          '&.MuiDialogTitle-root[style*="#1E40AF"], &.MuiDialogTitle-root[style*="#1e40af"], &.MuiDialogTitle-root[style*="primary"]': {
            color: '#FFFFFF !important',
            '& *': {
              color: '#FFFFFF !important',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Prompt", sans-serif',
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.92rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontFamily: '"Sarabun", "Prompt", sans-serif',
        },
      },
    },
  },
});

export default theme;
